import { ReactNode, Component, CSSProperties, Fragment } from 'react';
import _ from 'lodash';
import ReactSelect, { Props, ActionMeta, ThemeConfig } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { ExDialog } from './ExDialog';
import { DebugEx } from '@Utils/DebugEx';
import { isDefined, isFunction, IsNullOrEmpty, isStringEmpty, RunSafe } from '@Utils/Micro';
import { Localization, LocalizationMap } from './Localization/Localization';
import { GetSelectLocalizationDictionary, SelectLocalizationTextMapKey, SelectUILabelKey } from './SelectLocalization';
import { InputGroup, Input } from 'rsuite';
import { IconButton } from './Button';
import TextBox from './TextBox';
import Toggle from './Toggle';
import Checkbox from './Checkbox';
import { red200, grey500, grey800 } from '@Utils/Colors';
import ListItem from './ListItem';
// ================================================================================================
export type OptionValueTypes = string | number | boolean;
export type OptionValues = Array<OptionValueTypes>;
export type SelectOptions = Array<SelectOption>;
export type SelectOption = {
    // internal key for re-order
    key?: string;
    label: string;
    value?: OptionValueTypes;
    __isNew__?: boolean;
    options?: SelectOption[];
};

// todo(s)/question(s): 
// - should we move select helpers elsewhere? or extract
// consider
const SELECT_ALL_KEY = 'v/Af1IxWagZO=1h+3>[hTj:t??biW,';
const SELECT_DELIMETER = ',';
// ------------------------------------------------------------------------------------------------------
function valueContainAll(value: SelectOptions) {
    return isDefined(_.find(value, (v: SelectOption) => v?.value == SELECT_ALL_KEY));
}
// ------------------------------------------------------------------------------------------------------
function cleanValueArray(valueIn: SelectOptions, options: SelectOptions): SelectOptions {
    if (_.isArray(valueIn) && valueContainAll(valueIn))
        return options;
    return valueIn;
}
// ------------------------------------------------------------------------------------------------------
function toReactSelectValue(value: SelectOptions, options: SelectOptions): string {
    let str: string = '';
    if (!isDefined(value))
        return str;

    if (_.isArray(value)) {
        // Check if select all option is selected.
        if (valueContainAll(value)) {
            // Select all values for this select element.
            return _(options)
                .compact()
                .map(v => {
                    if (_.isArray(v.options))
                        return _.join(_.map(v.options, x => x.value));
                    return v.value;
                })
                .join(SELECT_DELIMETER);
        } else {
            // Concatenate the selected values.
            return _(value)
                .compact()
                .map(x => x.value)
                .join(SELECT_DELIMETER);
        }
    }
    else
        return _.toString((value as unknown as SelectOption).value);
}
// ================================================================================================
export interface SelectProps {
    valueStr: string;
    options: SelectOptions;
    isDisabled?: boolean;
    className?: string;
    isMulti: boolean;
    maxNumOfSelections?: number;
    closeMenuOnSelect?: boolean;
    isClearable?: boolean;
    isCreatable?: boolean;
    hideSelectedOptions?: boolean;
    controlStyle?: CSSProperties;
    menuStyle?: CSSProperties;
    menuPortalStyle?: CSSProperties;
    containerStyle?: CSSProperties;
    singleValueStyle?: CSSProperties;
    valueContainerStyle?: CSSProperties;
    multiValueStyle?: CSSProperties;
    placeholderStyle?: CSSProperties;
    inputStyle?: CSSProperties;
    optionStyle?: SelectOptionStyle;
    theme?: ThemeConfig;

    onChange: (value: string, valueInternal: SelectOptions) => void;
    formatGroupLabel?: (group: any) => ReactNode;

    localizationKey?: string;
    selectAllLabel?: string;
    placeholder?: string;
    hideSelectAll?: boolean; // (default: false)
    tabIndex?: number;
    noMenuPortal?: boolean;

    // opens the drop-down list in a dialog (useful for mobile apps).
    useDialog?: boolean;
    isSearchableWhenUsingDialog?: boolean;
    dialogTitle?: string;
}
// ------------------------------------------------------------------------------------------------
export interface SelectOptionStyle {
    backgroundColorFocused?: string;
    backgroundColorSelected?: string;
}
// ------------------------------------------------------------------------------------------------
export interface SelectState {
    valueStr: string;
    value: any;
    dialogIsOpen: boolean;
    newOption: string;
    searchValueInDialog: string;
}
// ------------------------------------------------------------------------------------------------
export class Select extends Component<SelectProps, SelectState> {
    // --------------------------------------------------------------------------------------------
    // needed to don't open the dialog when is clearable and the x icon is pressed
    dontOpenDialog: boolean = false;
    // --------------------------------------------------------------------------------------------
    constructor(props: SelectProps) {
        super(props);
        this.state = {
            valueStr: props.valueStr,
            dialogIsOpen: undefined,
            newOption: '',
            value: this.getReactSelectValue(props.valueStr),
            searchValueInDialog: ''
        };
    }
    // ------------------------------------------------------------------------------------------------------
    /**
     * Get select value(s) from value string
     */
    getReactSelectValue(valueStr: string): { values: SelectOptions, options: SelectOptions } {
        const options: SelectOption[] = _.cloneDeep(this.props.options);
        let selectValues: SelectOption[] = [];
        if (!isDefined(options) || !_.isArray(options))
            return { values: selectValues, options };

        if (this.props.isMulti) {
            let numberOfOptions = options.length;
            if (_.isArray(options[0]?.options)) {
                numberOfOptions = 0;
                _.forEach(options, x => numberOfOptions += x.options?.length);
            }

            // Split the selected values.
            const selectedOpts = _.map(_.split(valueStr, SELECT_DELIMETER), v => _.trim(v));

            // Select or/and create any options.
            _.forEach(selectedOpts, v => {
                var opt = undefined;

                for (const _v of options) {
                    if (isDefined(_v.value) && _v.value.toString() == v) {
                        opt = _v;
                    } else if (_.isArray(_v.options)) {
                        opt = _.find(_v.options, _v2 => {
                            return isDefined(_v2.value) &&
                                _v2.value.toString() == v;
                        });
                    }

                    if (opt != undefined)
                        break;
                }

                // note: The null is valid value
                if (opt != undefined)
                    selectValues.push(opt);
                else if (this.props.isCreatable &&
                    (_.isNumber(valueStr) || !_.isEmpty(valueStr)))
                    selectValues.push({
                        value: v,
                        label: v,
                        __isNew__: true
                    });
            });

            // Add select all option?
            if (this.props.hideSelectAll != true &&
                selectedOpts.length < numberOfOptions) {
                const labels: LocalizationMap = Select._GetLabelsFromLocalizationInfo(this.props.localizationKey);
                let selectAllLabel = isDefined(this.props.selectAllLabel) ? this.props.selectAllLabel : labels[SelectUILabelKey.SelectAll];

                if (
                    // don't append if select all label is undefined
                    isDefined(selectAllLabel) &&
                    // don't re-append same key value pair
                    _.findIndex(options, (val) => val.value === SELECT_ALL_KEY) === -1 &&
                    // don't add it when we are in dialog mode 
                    !this.props.useDialog
                )
                    options.unshift({ label: selectAllLabel, value: SELECT_ALL_KEY });
            }
        } else {
            // Find the selected option.
            var opt = undefined;
            for (const _v of options) {
                if (isDefined(_v.value) && _v.value.toString() == valueStr) {
                    opt = _v;
                } else if (_.isArray(_v.options)) {
                    opt = _.find(_v.options, _v2 => {
                        return isDefined(_v2.value) &&
                            _v2.value.toString() == valueStr;
                    });
                }

                if (opt != undefined)
                    break;
            }

            // Select or create or break out.
            if (opt != undefined)
                selectValues.push(opt);
            else if (this.props.isCreatable &&
                (_.isNumber(valueStr) || !_.isEmpty(valueStr)))
                selectValues.push({
                    value: valueStr,
                    label: valueStr,
                    __isNew__: true
                });
        }
        return { values: selectValues, options };
    }
    // --------------------------------------------------------------------------------------------
    componentDidUpdate(prevProps: SelectProps) {
        if (!_.isEqual(prevProps.valueStr, this.props.valueStr))
            this.setState({ valueStr: this.props.valueStr });
    }
    // --------------------------------------------------------------------------------------------
    setValueAndCloseDialog(value: string, valueInternal: SelectOptions) {
        this.setState({
            valueStr: value,
            dialogIsOpen: false,
            newOption: '',
            searchValueInDialog: ''
        }, () => {
            if (isFunction(this.props.onChange))
                this.props.onChange(this.state.valueStr, valueInternal);
        });
    }
    // --------------------------------------------------------------------------------------------
    renderSelect(isDialog: boolean) {
        const isMulti = this.props.isMulti;
        const labels: LocalizationMap = Select._GetLabelsFromLocalizationInfo(this.props.localizationKey);
        const selectValues = this.getReactSelectValue(this.state.valueStr);

        let props: Props = {
            className: this.props.className,
            isSearchable: true,
            isClearable: isDefined(this.props.isClearable) ? this.props.isClearable : true,
            hideSelectedOptions: isDefined(this.props.hideSelectedOptions) ? this.props.hideSelectedOptions : false,
            delimiter: SELECT_DELIMETER,
            isDisabled: this.props.isDisabled,
            placeholder: isDefined(this.props.placeholder) ? this.props.placeholder : labels[SelectUILabelKey.Placeholder],
            closeMenuOnSelect: this.props.closeMenuOnSelect,
            isMulti: isMulti,
            menuPlacement: 'auto',
            menuPortalTarget: this.props.noMenuPortal ? null : document.body,
            maxMenuHeight: 300,
            styles: {
                control: base => {
                    return {
                        ...base,
                        width: '100%',
                        overflow: 'auto',
                        ...this.props.controlStyle
                    };
                },
                menu: base => {
                    return {
                        ...base,
                        ...this.props.menuStyle
                    };
                },
                menuPortal: base => {
                    return {
                        ...base,
                        zIndex: 2000,
                        ...this.props.menuPortalStyle
                    };
                },
                container: base => {
                    return {
                        ...base,
                        ...this.props.containerStyle
                    };
                },
                singleValue: base => {
                    return {
                        ...base,
                        ...this.props.singleValueStyle
                    };
                },
                valueContainer: base => {
                    return {
                        ...base,
                        ...this.props.valueContainerStyle
                    };
                },
                multiValue: base => {
                    return {
                        ...base,
                        ...this.props.multiValueStyle
                    };
                },
                placeholder: base => {
                    return {
                        ...base,
                        ...this.props.placeholderStyle
                    };
                },
                input: base => {
                    return {
                        ...base,
                        ...this.props.inputStyle
                    };
                },
                option: (base, props) => {
                    let style = { ...base };
                    let optionStyle = this.props.optionStyle;
                    let backgroundColorFocused = optionStyle?.backgroundColorFocused;
                    let backgroundColorSelected = optionStyle?.backgroundColorSelected;

                    if (backgroundColorFocused != null && props.isFocused)
                        style.backgroundColor = backgroundColorFocused;
                    if (backgroundColorSelected != null && props.isSelected)
                        style.backgroundColor = backgroundColorSelected;

                    return style;
                }
            },
            theme: {
                borderRadius: 6,
                spacing: {
                    controlHeight: 34,
                    baseUnit: 2,
                    menuGutter: 2
                },
                ...this.props.theme
            },
            options: selectValues.options,
            value: selectValues.values as unknown,
            onChange: (val: unknown, action: ActionMeta<any>) => {

                // Check for limited selections?
                const limit = this.props.maxNumOfSelections;
                if (isMulti && _.isArray(val) && _.isNumber(limit)) {

                    // Is there the select all option?
                    if (valueContainAll(val)) {
                        // Break out if all options are more than the limit.
                        const opts = this.props.options;
                        if (_.isArray(opts) && opts.length > limit)
                            return;
                    }
                    // Break out if limit has exceeded.
                    else if (val.length > limit)
                        return;
                }

                const valueStr = toReactSelectValue(val as SelectOptions, this.props.options);
                this.setState({
                    valueStr: valueStr,
                    value: cleanValueArray(val as SelectOptions, this.props.options)
                }, () => {
                    if (isDefined(this.props.onChange))
                        RunSafe(() => this.props.onChange(this.state.valueStr, this.state.value));
                });
            },
            formatGroupLabel: this.props.formatGroupLabel,
            tabIndex: this.props.tabIndex
        } as Props;

        if (isDialog) {
            props.autoFocus = false;
            props.backspaceRemovesValue = false;
            props.menuIsOpen = false;
            props.menuPlacement = "bottom";
            props.menuPosition = 'absolute';
            props.menuPortalTarget = undefined;
            props.components = { DropdownIndicator: null };
            props.openMenuOnFocus = false;
            props.onFocus = event => {
                if (this.dontOpenDialog) {
                    setTimeout(() => event.target.blur(), 10);
                } else {
                    this.setState({ dialogIsOpen: true });
                }
                this.dontOpenDialog = false;
            };
            props.onChange = (value, action) => {
                if (action.action == 'clear') {
                    this.dontOpenDialog = true;

                    this.setState({ valueStr: '', value: [] }, () => {
                        if (isFunction(this.props.onChange)) {
                            this.props.onChange(this.state.valueStr, this.state.value);
                        }
                    });
                }
            };
            props.isSearchable = false;
            props.blurInputOnSelect = true;
        }

        if (this.props.isCreatable)
            return <CreatableSelect {...props} />
        else
            return <ReactSelect {...props} />;
    }
    // --------------------------------------------------------------------------------------------
    renderDialog() {
        if (!this.state.dialogIsOpen)
            return null;
        else
            return <ExDialog
                Enable={this.state.dialogIsOpen}
                Conf={{
                    title: this.props.dialogTitle,
                    handleClose: () => this.setValueAndCloseDialog(this.state.valueStr, this.state.value),
                    actions: [<IconButton
                        key='close'
                        tooltip='Close'
                        iconClassName='fas fa-times'
                        iconStyle={{ color: red200 }}
                        onClick={() => this.setValueAndCloseDialog(this.state.valueStr, this.state.value)} />],
                    bodyFunc: () => {
                        const isMulti = this.props.isMulti;
                        const isCreatable = this.props.isCreatable;
                        const isClearable = this.props.isClearable;
                        const isSearchable = this.props.isSearchableWhenUsingDialog;
                        const searchTerms = this.state.searchValueInDialog;

                        const origOpts = this.props.options;
                        const limit = this.props.maxNumOfSelections;

                        const labels = Select._GetLabelsFromLocalizationInfo(this.props.localizationKey);
                        const selectAllLabel = isDefined(this.props.selectAllLabel)
                            ? this.props.selectAllLabel
                            : labels[SelectUILabelKey.SelectAll];

                        const addOptionRow = (option: SelectOption) => {
                            const { values } = this.getReactSelectValue(this.state.valueStr);

                            let checked = false;
                            if (isDefined(_.find(values, v => v.value == option.value)))
                                checked = true;

                            if (isMulti)
                                return <ListItem
                                    primaryText={option.label}
                                    rightIcon={<Toggle
                                        toggled={checked}
                                        onToggle={isInputChecked => {
                                            const { values } = this.getReactSelectValue(this.state.valueStr);

                                            let existValue = _.find(values, v => v.value == option.value);
                                            if (!isInputChecked) {
                                                if (isDefined(existValue))
                                                    _.remove(values, v => v.value == option.value);
                                            } else {
                                                if (!isDefined(existValue)) {
                                                    let val = _.find(origOpts, v => v.value == option.value);
                                                    if (isDefined(val)) {
                                                        if (!_.isNumber(limit) || values.length < limit)
                                                            values.push(val);
                                                    }
                                                }
                                            }

                                            const valueStr = toReactSelectValue(values as SelectOptions, origOpts);
                                            this.setState({
                                                valueStr: valueStr,
                                                value: cleanValueArray(values, origOpts)
                                            });
                                        }}
                                    />}
                                />;
                            else
                                return <ListItem
                                    onClick={() => {
                                        let val = [_.find(origOpts, v => v.value == option.value)];
                                        const valueStr = toReactSelectValue(val, origOpts);
                                        val = cleanValueArray(val, origOpts);
                                        this.setValueAndCloseDialog(valueStr, val);
                                    }}
                                    primaryText={option.label}
                                    leftIcon={<Checkbox
                                        checkedIcon='fas fa-dot-circle'
                                        uncheckedIcon='far fa-circle'
                                        value={checked}
                                    />}
                                />;
                        };

                        const newOption = this.state.newOption;
                        return <div>
                            <div style={{
                                display: 'flex',
                                padding: '8px 16px 0 16px',
                                textAlign: 'right',
                                backgroundColor: '#f3f2f1',
                                justifyContent: 'flex-end'
                            }}>
                                {isCreatable
                                    ? <Fragment>
                                        {/* Text field to input new options. */}
                                        <TextBox
                                            fullWidth
                                            type='text'
                                            key='add-opt'
                                            placeholder={labels[SelectUILabelKey.NewOption]}
                                            value={newOption}
                                            onChangeString={newValue => this.setState({ newOption: newValue })} />
                                        {/* Button to add the new option. */}
                                        <IconButton
                                            tooltip={labels[SelectUILabelKey.Add]}
                                            disabled={isStringEmpty(newOption)}
                                            iconClassName='fas fa-plus-square'
                                            iconStyle={{ color: grey800 }}
                                            onClick={() => {
                                                // Break out if option exists or limit has exceeded.
                                                const { values: value } = this.getReactSelectValue(this.state.valueStr);
                                                if (_.find(value, v => v.value == newOption) != undefined ||
                                                    _.find(origOpts, v => v.value == newOption) != undefined ||
                                                    (_.isNumber(limit) && value.length == limit))
                                                    return;
                                                const newItem = [{
                                                    __isNew__: true,
                                                    label: newOption,
                                                    value: newOption
                                                }];
                                                let valueStr = toReactSelectValue(
                                                    isMulti ? _.concat(value, newItem) : newItem,
                                                    origOpts);
                                                let valueClean = cleanValueArray(value, origOpts);
                                                if (!isMulti)
                                                    this.setValueAndCloseDialog(valueStr, valueClean);
                                                else
                                                    this.setState({ valueStr: valueStr, value: valueClean });
                                            }} />
                                    </Fragment>
                                    : null}
                                {isMulti
                                    ? <IconButton
                                        tooltip={selectAllLabel}
                                        iconClassName='fas fa-toggle-on'
                                        iconStyle={{ color: grey800 }}
                                        onClick={() => {
                                            // Break out if limit has exceeded.
                                            if (_.isNumber(limit) &&
                                                _.isArray(origOpts) &&
                                                origOpts.length > limit)
                                                return;
                                            // Select all options.
                                            this.setState({
                                                valueStr: toReactSelectValue(
                                                    origOpts,
                                                    origOpts),
                                                value: origOpts
                                            });
                                        }} />
                                    : null}
                                {isClearable
                                    ? <IconButton
                                        tooltip={labels[SelectUILabelKey.Clear]}
                                        iconClassName='fas fa-times-square'
                                        iconStyle={{ color: grey800 }}
                                        onClick={() => {
                                            let valueStr = toReactSelectValue(null, origOpts);
                                            if (!isMulti)
                                                this.setValueAndCloseDialog(valueStr, null);
                                            else
                                                this.setState({ valueStr: valueStr, value: [] });
                                        }} />
                                    : null}
                            </div>
                            {isSearchable ? <div style={{
                                padding: '8px 16px',
                                backgroundColor: '#f3f2f1'
                            }}>
                                <InputGroup inside>
                                    <Input
                                        placeholder='Search'
                                        type='text'
                                        value={this.state.searchValueInDialog}
                                        onChange={value => this.setState({ searchValueInDialog: value })} />
                                    <InputGroup.Addon>
                                        <i className='fas fa-search' style={{ color: grey500 }} />
                                    </InputGroup.Addon>
                                </InputGroup>
                            </div> : null}

                            {/* new options */}
                            {_(this.getReactSelectValue(this.state.valueStr).values)
                                .filter(option => option.__isNew__ && (isSearchable
                                    ? option.label?.toLowerCase().indexOf(searchTerms?.toLowerCase()) > -1 : true))
                                .map(option => addOptionRow(option))
                                .value()}

                            {/* fixed options */}
                            {_.map(isSearchable
                                ? _.filter(origOpts, option => option.label?.toLowerCase().indexOf(searchTerms?.toLowerCase()) > -1)
                                : origOpts, option => addOptionRow(option))}</div>;
                    }
                }}
            />;
    }
    // --------------------------------------------------------------------------------------------
    render() {
        if (this.props.useDialog) {
            return <Fragment>
                {this.renderSelect(true)}
                {this.renderDialog()}
            </Fragment>
        } else {
            return this.renderSelect(false);
        }
    }
    // ------------------------------------------------------
    /**
     * Return label per each button key. 
     * Default values are taken from dictionary: `defaultLabelForUILabelKey`
     */
    private static _GetLabelsFromLocalizationInfo(localizationKey: string): LocalizationMap {
        // get default values
        const defaultValues = GetSelectLocalizationDictionary();

        // check for optional `localizationKey`
        if (!isDefined(localizationKey)) {
            // return default value
            return defaultValues;
        }

        // try get localization instance
        const localization = Localization.GetInstance(localizationKey);

        if (!isDefined(localization)) {
            DebugEx.TraceError("Select: passed `localizationKey`: " + localizationKey + " but could not find localization instance!");
            // fallback to defaults
            return defaultValues;
        }

        // instance found; try get text map
        const map = localization.GetTextMap(SelectLocalizationTextMapKey);
        if (!isDefined(map)) {
            DebugEx.TraceError("Select: passed `localizationKey`: " + localizationKey + " but could not get text map.");
            // fallback to defaults
            return defaultValues;
        }

        // ok! text map found! let's update response
        const res = defaultValues;
        _.forEach(map, (value, key) => {
            if (!IsNullOrEmpty(value))
                res[key] = value;
        });

        return res;
    }
    // --------------------------------------------------------------------------------------------
}
// ------------------------------------------------------------------------------------------------